/**
 * data-toggle is the parent
 * data-toggle-trigger is the trigger
 * data-toggle-auto-height is the element that will get the max-height changed on toggle
 * data-toggle-suffix is the suffix for the trigger and class if multiple toggles are needed
 * data-toggle-event is the event name that will be dispatched on toggle and you can listen to it with window.addEventListener('toggle:global', e => {})
 * data-toggle-class is the class that will be toggled. default is is-active
 * data-toggle-add-to-id is the id of the element that will get the class added to it
 */


// Tracks which hash has already been auto-scrolled to, so the load and
// tabs:activated passes don't double-scroll, while a genuine later hashchange
// (clicking an in-page #person-… link) still re-opens and re-scrolls.
//
// `settled` splits that per document.readyState: collapsed panels are pinned to
// their preview height by the `load` pass below, so anything scrolled to before
// that (tabs:activated fires at DOMContentLoaded) is measured against a page
// where every panel above it is still full height — and ends up hundreds of
// pixels short once they collapse. So a hash gets one scroll early (to be there
// immediately) and one more once the page is complete (to land accurately).
let scrolled = { hash: null, settled: false }

document.querySelectorAll('[data-toggle]').forEach(toggleParent => {

  const suffix = toggleParent.hasAttribute('data-toggle-suffix') ? '-' + toggleParent.getAttribute('data-toggle-suffix') : ''

  const triggers = toggleParent.querySelectorAll(`[data-toggle-trigger${suffix}]`)
  const className = toggleParent.hasAttribute(`data-toggle-class${suffix}`) ? toggleParent.getAttribute(`data-toggle-class${suffix}`) : 'is-active'

  const autoHeightElement = toggleParent.querySelector('[data-toggle-auto-height]')
  const collapsedPreviewElement = autoHeightElement ? autoHeightElement.querySelector('[data-toggle-collapsed-preview]') : null
  // Opt-in (data-toggle-hide-collapsed): while collapsed, remove the panel from
  // the a11y tree + tab order with `inert`, so SR/keyboard users don't wade
  // through hidden content (a11y audit #40/#50). Only for fully-collapsing
  // panels — a panel that keeps a visible preview must stay readable.
  const hideCollapsed = toggleParent.hasAttribute('data-toggle-hide-collapsed') && !collapsedPreviewElement
  const eventName = toggleParent.hasAttribute(`data-toggle-event${suffix}`) ? toggleParent.getAttribute(`data-toggle-event${suffix}`) : 'toggle:global'

  const addClassToElementSelector = toggleParent.hasAttribute(`data-toggle-add-to-id${suffix}`) ? toggleParent.getAttribute(`data-toggle-add-to-id${suffix}`) : false
  const addClassToElement = document.getElementById(addClassToElementSelector)

  const toggleHeight = () => {
    let isToggled = toggleParent.classList.contains(className)
    let event = new CustomEvent(eventName, {
      detail: {
        isToggled, toggleParent, triggers, className, autoHeightElement
      }
    })

    if (autoHeightElement && isToggled) {
      autoHeightElement.style.maxHeight = autoHeightElement.scrollHeight + 'px'

    } else if (autoHeightElement) {
      autoHeightElement.style.maxHeight = collapsedPreviewElement ? collapsedPreviewElement.offsetHeight + 'px' : 0
    }

    if (hideCollapsed && autoHeightElement) {
      if (isToggled) autoHeightElement.removeAttribute('inert')
      else autoHeightElement.setAttribute('inert', '')
    }

    window.dispatchEvent(event)
  }

  const syncAriaExpanded = () => {
    const isToggled = (addClassToElementSelector ? addClassToElement : toggleParent).classList.contains(className)
    // Always set the attribute (not only when it already exists) so triggers
    // whose markup omits aria-expanded still expose their open/closed state to
    // assistive tech (WCAG 4.1.2).
    triggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', isToggled ? 'true' : 'false')
    })
  }

  const toggle = e => {
    if(addClassToElementSelector != false) {
        addClassToElement.classList.toggle(className)
    } else {
        toggleParent.classList.toggle(className)
    }
    toggleHeight()
    syncAriaExpanded()
  }

  // Inbound deep-linking: if location.hash points at this toggle (or an element
  // nested inside it, e.g. a person article), open it and scroll it into view.
  // Read-only — this never writes the hash, so clicking a toggle stays silent.
  const applyFromHash = () => {
    const hash = location.hash.slice(1)
    if (!hash) return
    const target = document.getElementById(hash)
    if (!target) return
    if (target !== toggleParent && !toggleParent.contains(target)) return

    const classTarget = addClassToElementSelector ? addClassToElement : toggleParent
    // If we sit inside a still-hidden tab panel there's no layout yet; bail and
    // let the tabs:activated pass re-run this after the panel is un-hidden,
    // otherwise scrollHeight reads 0 and the panel stays collapsed.
    const measureEl = autoHeightElement || classTarget
    if (measureEl.offsetParent === null) return

    if (!classTarget.classList.contains(className)) {
      classTarget.classList.add(className)
      toggleHeight()
      syncAriaExpanded()
    }
    const settled = document.readyState === 'complete'
    if (scrolled.hash !== hash || (settled && !scrolled.settled)) {
      scrolled = { hash, settled }
      // Explicitly instant: html has `scroll-behavior: smooth`, and a smooth
      // scroll started during load gets cancelled by the panels collapsing
      // underneath it — leaving the page stranded halfway past the target.
      target.scrollIntoView({ behavior: 'instant' })
    }
  }

  //console.log('triggers', triggers)
  triggers.forEach(trigger => trigger.addEventListener('click', e => toggle(e)))
  syncAriaExpanded()

  window.addEventListener('resize', () => toggleHeight())
  window.addEventListener('load', () => toggleHeight())

  // A toggle inside a tab panel measures 0 while the panel is hidden, so its
  // collapsed preview gets pinned to max-height:0 on load. Recompute once the
  // panel is un-hidden and finally has layout.
  window.addEventListener('tabs:activated', () => toggleHeight())

  // Panel images are lazy-loaded (lazysizes) and ship without intrinsic
  // dimensions, so an open panel's scrollHeight is too short until they finish
  // loading — exactly the deep-link case: the card opens before its portrait
  // has loaded, max-height is pinned too low, and the image/meta get clipped.
  // Recompute when any descendant image loads. `load` doesn't bubble but does
  // fire in the capture phase, so one capturing listener catches every img.
  if (autoHeightElement) {
    autoHeightElement.addEventListener('load', () => {
      if (toggleParent.classList.contains(className)) toggleHeight()
    }, true)
  }

  window.addEventListener('hashchange', applyFromHash)     // genuine later in-page nav
  window.addEventListener('tabs:activated', applyFromHash) // in-tab: fires after un-hide
  window.addEventListener('load', applyFromHash)           // toggles that aren't inside a tab
})
